// Hooks da TELEFONIA da org: ramais, tronco de saída, identidade da chamada
// (RCD) e os toggles de discagem manual e click to call. Mesmo padrão de
// use-dialer.ts:
//   - `src/api/sip.ts` guarda o request (fetch + tipos), sem React;
//   - este arquivo guarda as CHAVES e os hooks do TanStack Query;
//   - a tela não importa a função de api: importa o hook.
//
// As chaves abaixo são as que já estavam espalhadas pelas telas, string por
// string — centralizar não é renomear. O React Query invalida por PREFIXO, e uma
// chave que divergisse uma letra viraria dado velho na tela, sem erro nenhum.
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deprovisionSipAgent,
  getClickToCall,
  getManualDial,
  getMySipCredentials,
  getOrgIdentity,
  getSipAgents,
  getSipTrunk,
  getSipTrunkHealth,
  provisionSipAgent,
  setClickToCall,
  setManualDial,
  setSipTrunkDid,
  type SipCredentials,
  type SipTrunkInfo,
} from "@/api/sip";

// As chaves DESTE domínio. Divergências herdadas que valem leitura — todas
// preservadas string por string, porque centralizar não é consertar:
//
//  - `agents` (["sip-agents"]) é lida por DUAS telas sem parentesco: Ramais SIP
//    (que também a invalida ao provisionar/remover) e Filas de Atendimento, que
//    só precisa de quem tem ramal para oferecer como membro. É a mesma entrada
//    de cache de propósito: quem abriu uma reaproveita na outra.
//  - `me` (["sip","me"]) é a ÚNICA chave em duas partes aqui, e é do softphone,
//    não da administração: as credenciais do próprio usuário. Não tem parentesco
//    de prefixo com `agents` — quem provisiona um ramal de terceiro não derruba
//    as credenciais de ninguém, e o softphone atualiza as suas à mão
//    (`setQueryData`) ao ressincronizar.
//  - `trunk` (["sip-trunk"]) e `trunkHealth` (["sip-trunk-health"]) NÃO são
//    prefixo uma da outra apesar do nome parecido: a sonda de saúde tem ritmo
//    próprio (poll de 60s + botão "verificar agora") e não deve ser derrubada
//    quando a bina muda.
//  - `manualDial` (["manual-dial"]) e `clickToCall` (["click-to-call"]) ficam
//    FORA do namespace "sip" mesmo os endpoints sendo /v1/sip/*. Divergência
//    pré-existente das telas; preservada porque mudá-la não muda só o nome, muda
//    quem invalida quem. A de click to call vinha de src/lib/click-to-call-enabled.ts
//    (fora das duas pontas, porque a NAVEGAÇÃO também a lê); o gate da navegação
//    continua lá e agora importa a chave daqui, para a string existir uma vez só.
export const sipKeys = {
  agents: ["sip-agents"] as const,
  me: ["sip", "me"] as const,
  trunk: ["sip-trunk"] as const,
  trunkHealth: ["sip-trunk-health"] as const,
  orgIdentity: ["sip-org-identity"] as const,
  manualDial: ["manual-dial"] as const,
  clickToCall: ["click-to-call"] as const,
};

// --- Ramais ---

// A listagem é gateada por `enabled` nas duas telas que a leem: só gestor lê
// /v1/sip/* (403 para vendedor), e o papel chega depois de /v1/users.
export function useSipAgents(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: sipKeys.agents,
    queryFn: () => getSipAgents(),
    enabled: options.enabled,
  });
}

// Credenciais do ramal do PRÓPRIO usuário, para o softphone. As opções não são
// enfeite: `retry: false` porque "não tem ramal" é uma resposta honesta e não
// adianta insistir; `staleTime: Infinity` porque a credencial é estável durante
// a sessão e refazer o GET reabriria o registro SIP sem motivo.
export function useMySipCredentials() {
  return useQuery({
    queryKey: sipKeys.me,
    queryFn: () => getMySipCredentials(),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

// Grava as credenciais recém-provisionadas no cache sem passar por um GET novo.
// É `useCallback` porque o softphone a usa dentro de outro `useCallback`
// (`reload`), que entra em arrays de dependência: uma identidade nova a cada
// render recriaria a cadeia inteira de callbacks do hook a cada render.
export function useSetMySipCredentials() {
  const queryClient = useQueryClient();
  return useCallback(
    (credentials: SipCredentials) => queryClient.setQueryData(sipKeys.me, credentials),
    [queryClient],
  );
}

// A invalidação da lista é a mesma para provisionar e para remover, então é do
// hook. Os dois `mutationFn` chamam a função de api dentro de uma seta: o
// TanStack invoca o mutationFn com (variáveis, contexto), e uma função de api
// que aceite um segundo parâmetro receberia o contexto como se fosse dado.
function useInvalidateSipAgents() {
  const queryClient = useQueryClient();
  return useCallback(
    () => void queryClient.invalidateQueries({ queryKey: sipKeys.agents }),
    [queryClient],
  );
}

// Provisionar o ramal de um membro. O toast da tela precisa do `sipUsername` que
// volta do back, então o handler recebe as credenciais.
export function useProvisionSipAgent(
  handlers: {
    onSuccess?: (credentials: SipCredentials) => void;
    onError?: (error: Error) => void;
  } = {},
) {
  const invalidate = useInvalidateSipAgents();
  return useMutation({
    mutationFn: (userId: string) => provisionSipAgent(userId),
    onSuccess: (credentials) => {
      handlers.onSuccess?.(credentials);
      invalidate();
    },
    onError: handlers.onError,
  });
}

export function useDeprovisionSipAgent(
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const invalidate = useInvalidateSipAgents();
  return useMutation({
    mutationFn: (userId: string) => deprovisionSipAgent(userId),
    onSuccess: () => {
      handlers.onSuccess?.();
      invalidate();
    },
    onError: handlers.onError,
  });
}

// --- Tronco de saída ---

// Duas telas leem o tronco pela MESMA chave: Troncos (visão read-only) e
// Configurações › Telefone (que só quer o `orgDid` para preencher a bina).
export function useSipTrunk(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: sipKeys.trunk,
    queryFn: () => getSipTrunk(),
    enabled: options.enabled,
  });
}

// Sonda de saúde do VoIP: poll de 60s, sem refetch no foco (a tela tem um botão
// "verificar agora" explícito). Devolve `null` quando o back não expõe o
// healthcheck — ausência honesta, não erro.
export function useSipTrunkHealth(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: sipKeys.trunkHealth,
    queryFn: () => getSipTrunkHealth(),
    enabled: options.enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });
}

// Salvar a bina INVALIDA O CACHE INTEIRO (`invalidateQueries()` sem chave).
// É o comportamento que a tela de Telefone sempre teve: o DID entra na `key` do
// formulário e é lido por várias telas de telefonia, e a tela preferiu derrubar
// tudo a enumerar. Divergência pré-existente — preservada aqui, e não
// "consertada" para `sipKeys.trunk`: trocar isso mudaria o que a tela mostra
// depois de salvar, não só o nome de uma chave.
export function useSetSipTrunkDid(
  handlers: { onSuccess?: (trunk: SipTrunkInfo) => void; onError?: (error: Error) => void } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (callerDid: string) => setSipTrunkDid(callerDid),
    onSuccess: (trunk) => {
      handlers.onSuccess?.(trunk);
      void queryClient.invalidateQueries();
    },
    onError: handlers.onError,
  });
}

// --- Identidade da chamada (RCD) ---

// Só a LEITURA vira hook. O `setOrgIdentity` continua sendo chamado direto de
// `@/api/sip` pela tela de marca: lá ele roda dentro de um `mutationFn` que
// também atualiza a organização no Better Auth, e só escreve se aquele bloco
// específico ficou sujo. Envolver isso num hook de mutação separado quebraria a
// regra de "cada bloco só escreve se mudou" — mesmo caso da importação de CSV
// em use-leads.ts.
export function useOrgIdentity(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: sipKeys.orgIdentity,
    queryFn: () => getOrgIdentity(),
    enabled: options.enabled,
  });
}

// --- Discagem manual ---

// Duas telas leem o toggle e divergem no gate, de propósito: Configurações ›
// Discador passa `enabled: canManage` (o card é do gestor), e o softphone lê
// SEM gate nenhum — o vendedor precisa saber se o teclado dele está liberado.
// `options` vazio deixa `enabled: undefined`, que para o TanStack é ligado, que
// é exatamente o que o softphone fazia inline.
export function useManualDial(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: sipKeys.manualDial,
    queryFn: () => getManualDial(),
    enabled: options.enabled,
  });
}

// Sem invalidação: a resposta do PUT já é o estado novo, e a tela o grava no
// cache (`setQueryData`) para o switch virar sem esperar um GET. Refazer o GET
// aqui devolveria o mesmo valor por um round-trip a mais.
export function useSetManualDial(
  handlers: {
    onSuccess?: (result: { manualDialEnabled: boolean }) => void;
    onError?: (error: Error) => void;
  } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => setManualDial(enabled),
    onSuccess: (result) => {
      queryClient.setQueryData(sipKeys.manualDial, result);
      handlers.onSuccess?.(result);
    },
    onError: handlers.onError,
  });
}

// --- Click to call ---

// O toggle que liga o GRUPO "Click to call" no menu e as telas de operação. Três
// leitores, três gates diferentes, todos na mesma entrada de cache: a tela de
// Integrações passa `enabled: canManage` (o card é do gestor), a tela Geral lê
// sem gate (quem chegou pela rota precisa saber se o recurso está ligado) e a
// navegação lê pelo `useClickToCallEnabled` de src/lib/click-to-call-enabled.ts,
// que mantém `retry: false` e colapsa o resultado num booleano — aquele hook
// segue lá porque quem o consome é a navegação, e não uma tela de telefonia.
export function useClickToCall(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: sipKeys.clickToCall,
    queryFn: getClickToCall,
    enabled: options.enabled,
  });
}

// Sem invalidação, igual ao manual-dial acima: a resposta do PUT já é o estado
// novo, e ela vai direto para o cache (`setQueryData`) para o switch — e o grupo
// no menu, que lê a mesma chave — virar sem esperar um GET.
export function useSetClickToCall(
  handlers: {
    onSuccess?: (result: { clickToCallEnabled: boolean }) => void;
    onError?: (error: Error) => void;
  } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => setClickToCall(enabled),
    onSuccess: (result) => {
      queryClient.setQueryData(sipKeys.clickToCall, result);
      handlers.onSuccess?.(result);
    },
    onError: handlers.onError,
  });
}
